<template>
  <l-map 
    :minZoom="minZoom" 
    ref="map" 
    style="height: 100%; z-index: 0"
    :zoom="zoom" 
    :center="center"
  >
    <!-- Base Layer -->
    <l-tile-layer 
      v-for="(baseMap, name) in baseMaps"
      :key="name"
      :visible="selectedBaseMap === name"
      :url="baseMap.url"
      :attribution="baseMap.attribution" 
      :maxZoom="baseMap.maxZoom"
    ></l-tile-layer>


    <!-- Main GeoJSON Layer -->
    <l-geo-json
      v-if="filteredGeoJSON"
      :geojson="filteredGeoJSON"
      :options-style="styleFeature"
      :options="{
        onEachFeature: onEachFeature
      }"
    ></l-geo-json>

    <!-- Controls -->
    <l-control-scale 
      position="bottomright" 
      :imperial="false"
    ></l-control-scale>
    

    <!-- Legend Control -->
    <l-control position="bottomleft">
      <div class="legend-control" :class="{ 'legend-collapsed': isLegendCollapsed }">
        <div class="legend-header" @click="toggleLegend">
          <span>Legend</span>
          <v-icon small>{{ isLegendCollapsed ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </div>
        <div v-show="!isLegendCollapsed" class="legend-content">
          <!-- Utilities Legend -->
          <div class="legend-section">
            <h4>Utilities</h4>
            <div v-for="(color, block) in utilityColors" :key="block" class="legend-item">
              <span class="color-box" :style="{ backgroundColor: color }"></span>
              <span class="legend-label">{{ block }}</span>
            </div>
          </div>
          <!-- Plots Legend -->
          <div class="legend-section">
            <h4>Plots</h4>
            <div v-for="(color, dimension) in dimensionColors" :key="dimension" class="legend-item">
              <span class="color-box" :style="{ backgroundColor: color }"></span>
              <span class="legend-label">{{ dimension }}</span>
            </div>
          </div>
          <!-- Categories Legend -->
          <!-- <div class="legend-section">
            <h4>Categories</h4>
            <div v-for="(color, category) in categoryColors" :key="category" class="legend-item">
              <span class="color-box" :style="{ backgroundColor: color }"></span>
              <span class="legend-label">{{ category }}</span>
            </div>
          </div> -->
        </div>
      </div>
    </l-control>
  </l-map>
</template>

<script>
import { mapGetters } from 'vuex';
import { LMap, LTileLayer, LControlLayers, LControl, LGeoJson, LControlScale } from 'vue2-leaflet';

export default {
  name: 'GeoJsonMap',

  data() {
    return {
      utilitiesData: null,
      selectedBaseMap: 'OpenStreetMap',
      showUtilities: true,
      isLayerControlCollapsed: false,
      minZoom: 5,
      zoom: 15.5,
      // center: [25.245890454338806, 62.26724249738134],
      center: [33.541393, 72.946702],
      maxBounds: L.latLngBounds([
        [37.0841, 60.8729],
        [23.6345, 77.8374]
      ]),
      baseMaps: {
        'OpenStreetMap': {
          url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
          maxZoom: 19,
          attribution: '© OpenStreetMap contributors'
        },
        'Dark Mode': {
          url: 'https://tiles.stadiamaps.com/tiles/alidade_smooth_dark/{z}/{x}/{y}{r}.png',
          maxZoom: 20,
          attribution: '© Stadia Maps'
        },
        'Satellite': {
          url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
          maxZoom: 19,
          attribution: '© Esri'
        }
      },
      isLegendCollapsed: true,
      utilityColors: {
        'Community Center': '#FF7043',
        'Future extension': '#81C784',
        'GA': '#64B5F6',
        'GY': '#4DB6AC',
        'Mosque': '#9575CD',
        'Parking': '#FFB74D',
        'Play Ground': '#4CAF50',
        'Road': '#BDBDBD',
        'School': '#7986CB'
      },
      dimensionColors: {
        'Residential': '#87c7ff',
        'Commercial': '#E53935',
        // '50 x 90': '#E53935',
        // '40 x 60': '#FDD835',
        // '25 x 45': '#8E24AA'
      },
      categoryColors: {
        'Community Center': '#FF7043',
        'Educational Complex': '#9C27B0',
        'Green/Open Space': '#4CAF50',
        'Mosque': '#2196F3',
        'Park/Playground': '#8BC34A',
        'Road': '#9E9E9E',
        'School': '#3F51B5'
      }
    };
  },

  computed: {
    ...mapGetters('geojson', ['filteredGeoJSON'])
  },

  methods: {
    async loadUtilitiesData() {
      try {
        const response = await fetch('data/silvercity/utilities.geojson');
        const data = await response.json();
        this.utilitiesData = data;
      } catch (error) {
        console.error('Error loading utilities:', error);
      }
    },

    styleUtility(feature) {
      const baseStyle = {
        weight: 1,
        opacity: 1,
        fillOpacity: 0.7,
        color: '#666'
      };

      const props = feature.properties;
      if (!props) return baseStyle;

      return {
        ...baseStyle,
        fillColor: this.utilityColors[props.block] || '#757575'
      };
    },

    onEachFeatureUtility(feature, layer) {
      if (feature.properties.block !== 'Road') {
        layer.on({
          mouseover: (e) => {
            const layer = e.target;
            layer.setStyle({
              weight: 2,
              fillOpacity: 0.9
            });
          },
          mouseout: (e) => {
            const layer = e.target;
            layer.setStyle(this.styleUtility(feature));
          }
        });
      }

      layer.bindTooltip(feature.properties.block || '', {
        permanent: false,
        sticky: true,
        direction: 'top'
      });

      layer.on('click', (e) => {
        this.showUtilityPopup(feature, e.latlng);
      });
    },

    showUtilityPopup(feature, latlng) {
      const props = feature.properties;
      if (!props) return;

      const content = `
        <div class="feature-popup">
          <h4 style="color: #a24187; margin-bottom: 10px;">
            ${props.block || 'Utility'}
          </h4>
          <table style="border-collapse: collapse; width: 100%;">
            <tr>
              <td style="padding: 4px 8px; border-bottom: 1px solid #eee;">Type:</td>
              <td style="padding: 4px 8px; border-bottom: 1px solid #eee;"><b>${props.block}</b></td>
            </tr>
            ${props.area ? `
            <tr>
              <td style="padding: 4px 8px; border-bottom: 1px solid #eee;">Area:</td>
              <td style="padding: 4px 8px; border-bottom: 1px solid #eee;"><b>${Math.round(props.area)} sq.m</b></td>
            </tr>
            ` : ''}
            ${props.plot_id ? `
            <tr>
              <td style="padding: 4px 8px;">Plot ID:</td>
              <td style="padding: 4px 8px;"><b>${props.plot_id}</b></td>
            </tr>
            ` : ''}
          </table>
        </div>
      `;

      L.popup({
        maxWidth: 300,
        className: 'custom-popup'
      })
      .setLatLng(latlng)
      .setContent(content)
      .openOn(this.$refs.map.mapObject);
    },

    styleFeature(feature) {
      const baseStyle = {
        weight: 1,
        opacity: 1,
        fillOpacity: 0.7,
        color: '#666'
      };

      const props = feature.properties;
      if (!props) return baseStyle;

      if (props.Category === 'Residential' || 'Commercial') {
        return {
          ...baseStyle,
          fillColor: this.dimensionColors[props.Category] || '#9c6d48'
        };
      }
      return {
        ...baseStyle,
        fillColor: this.categoryColors[props.Category] || '#757575'
      };
    },

    onEachFeature(feature, layer) {
      if (feature.properties.Category !== 'Road') {
        layer.on({
          mouseover: (e) => {
            const layer = e.target;
            layer.setStyle({
              weight: 2,
              fillOpacity: 0.9
            });
          },
          mouseout: (e) => {
            const layer = e.target;
            layer.setStyle(this.styleFeature(feature));
          }
        });
      }

      layer.on('click', (e) => {
        this.showFeaturePopup(feature, e.latlng);
      });

      if (feature.properties && feature.properties.plot_id) {
        layer.bindTooltip(feature.properties.plot_id, {
          permanent: false,
          sticky: true,
          direction: 'top'
        });
      }
    },

    showFeaturePopup(feature, latlng) {
      const props = feature.properties;
      if (!props) return;

      const content = `
        <div class="feature-popup">
          <!-- Header Section -->
          <div class="popup-header" style="background: linear-gradient(135deg, #a24187, #4062bb); padding: 12px; border-radius: 8px 8px 0 0; margin: -12px -12px 12px -12px;">
            <h4 style="color: white; margin: 0;">
              Plot ID: ${props.plot_id || props.Category}
            </h4>
          </div>

          <!-- Image Gallery -->
          <div class="image-gallery" style="position: relative; height: 200px; margin-bottom: 12px; border-radius: 4px; overflow: hidden;">
            <img src="img/plots/land4.png" class="gallery-img" style="width: 100%; height: 100%; object-fit: cover; display: block; position: absolute; transition: opacity 0.3s;" 
                 id="img1-${props.plot_id}" />
            <img src="img/plots/land2.png" class="gallery-img" style="width: 100%; height: 100%; object-fit: cover; display: block; position: absolute; opacity: 0; transition: opacity 0.3s;" 
                 id="img2-${props.plot_id}" />
            <img src="img/plots/land3.png" class="gallery-img" style="width: 100%; height: 100%; object-fit: cover; display: block; position: absolute; opacity: 0; transition: opacity 0.3s;" 
                 id="img3-${props.plot_id}" />
            <img src="img/plots/land1.png" class="gallery-img" style="width: 100%; height: 100%; object-fit: cover; display: block; position: absolute; opacity: 0; transition: opacity 0.3s;" 
                 id="img4-${props.plot_id}" />
            
            <!-- Gallery Controls -->
            <div style="position: absolute; bottom: 10px; right: 10px; display: flex; gap: 8px;">
              <button onclick="
                document.getElementById('img1-${props.plot_id}').style.opacity=1;
                document.getElementById('img2-${props.plot_id}').style.opacity=0;
                document.getElementById('img3-${props.plot_id}').style.opacity=0;
                document.getElementById('img4-${props.plot_id}').style.opacity=0;" 
                      style="background: rgba(255,255,255,0.8); border: none; width: 24px; height: 24px; border-radius: 12px; cursor: pointer; font-size: 12px;">1</button>
              <button onclick="
                document.getElementById('img1-${props.plot_id}').style.opacity=0;
                document.getElementById('img2-${props.plot_id}').style.opacity=1;
                document.getElementById('img3-${props.plot_id}').style.opacity=0;
                document.getElementById('img4-${props.plot_id}').style.opacity=0;" 
                      style="background: rgba(255,255,255,0.8); border: none; width: 24px; height: 24px; border-radius: 12px; cursor: pointer; font-size: 12px;">2</button>
              <button onclick="
                document.getElementById('img1-${props.plot_id}').style.opacity=0;
                document.getElementById('img2-${props.plot_id}').style.opacity=0;
                document.getElementById('img3-${props.plot_id}').style.opacity=1;
                document.getElementById('img4-${props.plot_id}').style.opacity=0;" 
                      style="background: rgba(255,255,255,0.8); border: none; width: 24px; height: 24px; border-radius: 12px; cursor: pointer; font-size: 12px;">3</button>
              <button onclick="
                document.getElementById('img1-${props.plot_id}').style.opacity=0;
                document.getElementById('img2-${props.plot_id}').style.opacity=0;
                document.getElementById('img3-${props.plot_id}').style.opacity=0;
                document.getElementById('img4-${props.plot_id}').style.opacity=1;" 
                      style="background: rgba(255,255,255,0.8); border: none; width: 24px; height: 24px; border-radius: 12px; cursor: pointer; font-size: 12px;">4</button>
            </div>
          </div>

          <!-- Plot Details Table -->
          <table style="border-collapse: collapse; width: 100%;">
            <tr>
              <td style="padding: 4px 8px; border-bottom: 1px solid #eee;">Category:</td>
              <td style="padding: 4px 8px; border-bottom: 1px solid #eee;"><b>${props.Category}</b></td>
            </tr>
            ${props.Dimensions ? `
            <tr>
              <td style="padding: 4px 8px; border-bottom: 1px solid #eee;">Dimensions:</td>
              <td style="padding: 4px 8px; border-bottom: 1px solid #eee;"><b>${props.Dimensions}</b></td>
            </tr>
            ` : ''}
            <tr>
              <td style="padding: 4px 8px; border-bottom: 1px solid #eee;">Zone:</td>
              <td style="padding: 4px 8px; border-bottom: 1px solid #eee;"><b>${props.Zone}</b></td>
            </tr>
            ${props.J_Owner ? `
            <tr>
              <td style="padding: 4px 8px; border-bottom: 1px solid #eee;">Owner:</td>
              <td style="padding: 4px 8px; border-bottom: 1px solid #eee;"><b>${props.J_Owner}</b></td>
            </tr>
            ` : ''}
            <tr>
              <td style="padding: 4px 8px; border-bottom: 1px solid #eee;">Status:</td>
              <td style="padding: 4px 8px; border-bottom: 1px solid #eee;">
                <span style="
                  padding: 2px 8px;
                  border-radius: 12px;
                  background-color: ${props.SoldStatus === 'Sold' ? '#4CAF50' : '#FFA726'};
                  color: white;
                  font-size: 0.875rem;
                ">
                  ${props.SoldStatus}
                </span>
              </td>
            </tr>
            <tr>
              <td style="padding: 4px 8px; border-bottom: 1px solid #eee;">Valuation:</td>
              <td style="padding: 4px 8px; border-bottom: 1px solid #eee;">
                <b>PKR 7.4 M</b>
              </td>
            </tr>
            <tr>
              <td style="padding: 4px 8px;">Payment Status:</td>
              <td style="padding: 4px 8px;">
                <div style="background: #f5f5f5; border-radius: 12px; padding: 8px;">
                  <div style="margin-bottom: 4px; font-size: 0.875rem;">
                    <span style="color: #4CAF50; font-weight: bold;">✓</span> Down Payment
                  </div>
                  <div style="margin-bottom: 4px; font-size: 0.875rem;">
                    <span style="color: #4CAF50; font-weight: bold;">✓</span> First Installment
                  </div>
                  <div style="margin-bottom: 4px; font-size: 0.875rem;">
                    <span style="color: #FFA726; font-weight: bold;">⋯</span> Second Installment
                  </div>
                  <div style="font-size: 0.875rem;">
                    <span style="color: #999; font-weight: bold;">○</span> Final Payment
                  </div>
                </div>
              </td>
            </tr>
          </table>
        </div>
      `;

      L.popup({
        maxWidth: 300,
        className: 'custom-popup'
      })
      .setLatLng(latlng)
      .setContent(content)
      .openOn(this.$refs.map.mapObject);
    },

    toggleLegend() {
      this.isLegendCollapsed = !this.isLegendCollapsed;
    }
  },

  async mounted() {
    await Promise.all([
      this.$store.dispatch('geojson/loadGeoJSONData'),
      this.loadUtilitiesData()
    ]);

    this.$nextTick(() => {
      const map = this.$refs.map.mapObject;
      const baseMaps = {};
      const overlayMaps = {
        'Utilities': L.geoJSON(this.utilitiesData, {
          style: this.styleUtility,
          onEachFeature: this.onEachFeatureUtility
        })
      };
      
      Object.entries(this.baseMaps).forEach(([name, config]) => {
        baseMaps[name] = L.tileLayer(config.url, {
          maxZoom: config.maxZoom,
          attribution: config.attribution
        });
      });

      L.control.layers(baseMaps, overlayMaps, {
        position: 'topright',
        collapsed: true
      }).addTo(map);

      baseMaps[this.selectedBaseMap].addTo(map);
      overlayMaps['Utilities'].addTo(map); // Show utilities by default
    });
  }
};
</script>

<style>
.custom-popup .leaflet-popup-content-wrapper {
  border-radius: 8px;
  padding: 0;
  overflow: hidden;
  min-width: 300px;
  box-shadow: 0 3px 14px rgba(0,0,0,0.2);
}


.custom-popup .leaflet-popup-content {
  margin: 0;
  padding: 12px;
}
.custom-popup .leaflet-popup-tip-container {
  margin-top: -1px;
}

.feature-popup {
  min-width: 250px;
}

.feature-popup h4 {
  margin: 0 0 8px 0;
  padding: 0;
}

.legend-control {
  background: white;
  padding: 6px;
  border-radius: 4px;
  box-shadow: 0 1px 5px rgba(0,0,0,0.4);
  min-width: 200px;
  max-width: 300px;
}

.legend-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  padding: 4px;
  font-weight: bold;
  color: #666;
}

.legend-content {
  margin-top: 8px;
  padding: 4px;
}

.legend-section {
  margin-bottom: 12px;
}

.legend-section h4 {
  margin-bottom: 8px;
  font-size: 0.9rem;
  color: #666;
}

.legend-item {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}

.color-box {
  width: 16px;
  height: 16px;
  border-radius: 3px;
  margin-right: 8px;
  border: 1px solid rgba(0,0,0,0.1);
}

.legend-label {
  font-size: 0.85rem;
  color: #333;
}

.legend-collapsed .legend-content {
  display: none;
}
</style>